import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';

// ---------------------------------------------------------------------------
// Transport DTOs for the HubSpot Lead Status mapping UI (Teil 2).
//
// The mapping panel shows the portal's REAL hs_lead_status options and one row
// per local state (contacted / replied / bounced / opted_out) with the saved
// value and a suggested value to prefill. Pure transport — no _id / collection.
// ---------------------------------------------------------------------------

@Entity()
export class HubspotStatusOptionView extends AdornisEntity {
  static override _class = 'HubspotStatusOptionView';

  // Returned inside arrays; baseQL needs a stable identity to merge them. The
  // portal option value is unique within the property.
  static override resolveID(entity: { value?: string }): string {
    if (!entity.value) throw new Error('HubspotStatusOptionView is missing value');
    return entity.value;
  }

  @Field(type => String) value!: string;
  @Field(type => String) label!: string;
}

@Entity()
export class HubspotLeadStatusRowView extends AdornisEntity {
  static override _class = 'HubspotLeadStatusRowView';

  // One row per local status; the status is the stable identity for array merge.
  static override resolveID(entity: { localStatus?: string }): string {
    if (!entity.localStatus) throw new Error('HubspotLeadStatusRowView is missing localStatus');
    return entity.localStatus;
  }

  /** 'contacted' | 'replied' | 'bounced' | 'opted_out' */
  @Field(type => String) localStatus!: string;
  /** Human label for the local state. */
  @Field(type => String) localLabel!: string;
  /** The currently confirmed value ('' = not set). */
  @Field(type => String) savedValue!: string;
  /** A value to prefill when nothing is saved yet ('' = no suggestion). */
  @Field(type => String) suggestedValue!: string;
}

@Entity()
export class HubspotLeadStatusMappingView extends AdornisEntity {
  static override _class = 'HubspotLeadStatusMappingView';

  // One view per connection; merge get / suggest / re-get for the same
  // connection in place rather than accumulating.
  static override resolveID(entity: { connectionId?: string }): string {
    if (!entity.connectionId) throw new Error('HubspotLeadStatusMappingView is missing connectionId');
    return entity.connectionId;
  }

  @Field(type => String) connectionId!: string;
  /** Could the portal's hs_lead_status options be read? (false → scope / connection issue) */
  @Field(type => Boolean) optionsReadable!: boolean;
  /** When not readable, the error text to show. */
  @Field(type => String) readError?: string;
  /** Whether the suggested values came from AI (true) or the deterministic resolver (false). */
  @Field(type => Boolean) aiSuggested!: boolean;
  /** Whether a confirmed mapping is already saved on the connection. */
  @Field(type => Boolean) hasSaved!: boolean;
  @Field(type => [HubspotStatusOptionView]) options!: HubspotStatusOptionView[];
  @Field(type => [HubspotLeadStatusRowView]) rows!: HubspotLeadStatusRowView[];
}
